import { PlanPrice } from './checkout.service';

/**
 * Arithmétique des prix, isolée du service pour être testable seule.
 *
 * Ces deux calculs remplacent des mentions qui étaient écrites en dur. C'est le
 * cœur du problème : « ≈ 2 mois offerts » n'est vrai que pour un couple de prix
 * donné. Avec 3,99 €/mois et 39,99 €/an l'économie fait bien 2 mois ; avec
 * 5 €/mois et le même annuel, elle en fait 4. Un magasin fixant ses paliers par
 * pays, la mention doit se recalculer partout.
 */

/**
 * Économie de la formule annuelle, exprimée en mois de la formule mensuelle.
 *
 * `null` quand le calcul n'a pas de sens — l'appelant masque alors la mention
 * plutôt que d'afficher un chiffre faux :
 * - un prix manque (le fournisseur n'a rien rendu) ;
 * - les deux devises diffèrent (les comparer ne veut rien dire) ;
 * - l'annuel n'est pas avantageux (aucune économie à annoncer).
 */
export function monthsFreeOf(
  monthly: PlanPrice | undefined,
  yearly: PlanPrice | undefined,
): number | null {
  if (!monthly || !yearly) return null;
  if (monthly.amount <= 0 || yearly.amount <= 0) return null;
  if (monthly.currency !== yearly.currency) return null;

  const months = Math.round(
    (12 * monthly.amount - yearly.amount) / monthly.amount,
  );
  return months > 0 ? months : null;
}

/**
 * Économie de la formule annuelle, en unités de la devise, arrondie à l'unité
 * (« 8 € d'économie » pour 3,99 € et 39,99 €). Mêmes refus que `monthsFreeOf`.
 */
export function yearlySavingOf(
  monthly: PlanPrice | undefined,
  yearly: PlanPrice | undefined,
): number | null {
  if (!monthly || !yearly) return null;
  if (monthly.amount <= 0 || yearly.amount <= 0) return null;
  if (monthly.currency !== yearly.currency) return null;
  const saving = Math.round(12 * monthly.amount - yearly.amount);
  return saving > 0 ? saving : null;
}

/**
 * Montant mis en forme selon la locale.
 *
 * `Intl` connaît le nombre de décimales de chaque devise : le yen s'écrit sans
 * centimes, l'euro avec. Formater à la main produirait « 500,00 ¥ ».
 */
export function formatAmount(
  amount: number,
  currency: string,
  locale: string,
  wholeUnits = false,
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      // Une économie arrondie s'écrit « 8 € », pas « 8,00 € ».
      ...(wholeUnits ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}),
    }).format(amount);
  } catch {
    // Devise inconnue d'`Intl` : le montant brut vaut mieux que rien.
    return `${amount.toFixed(2)} ${currency}`;
  }
}
